/* eslint-disable react-hooks/exhaustive-deps */
import { Checkbox, Grid, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { AppTable, DeleteConfirmDialog } from "components/molecules";
import { ColorHelper, StringHelper, TableHelper } from "helpers";
import { usePaginationRequest } from "hooks/useRequest";
import {
  EProductType,
  IDownloadProductMasterDataRequest,
  IGetAllProductRequest,
  IPaginateApiResponse,
  IProduct,
} from "model";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { DownloadActions, ProductActions } from "redux/actions";
import { useAppSelector } from "redux/configurations/reduxHooks";
import { ProductApiService } from "services";
import { CURRENT_PAGE_SIZE, EXPORT_FILE_NAME, FIRST_PAGE } from "utils/Const";
import { I18nTranslator, translations } from "utils/i18n";
import FilterPopup from "./components/FilterPopup";

const ProductConfiguration = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const action = useAppSelector((state) => state.ProductReducer.action);
  const downloading = useAppSelector(
    (state) => state.AppConfigReducer.downloading
  );
  const [open, setOpen] = useState(false);
  const [itemSelected, setItemSelected] = useState<IProduct>();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [currentProductType, setCurrentProductType] = useState<
    EProductType | undefined
  >(EProductType.ALL);
  const [currentActive, setCurrentActive] = useState<boolean | any>("A");
  const [currentQRType, setCurrentQRType] = useState<boolean | any>("A");
  const [refresh, setRefresh] = useState<boolean>(false);
  const [request, setRequest] = useState<IGetAllProductRequest>({
    pageNumber: FIRST_PAGE,
    pageSize: CURRENT_PAGE_SIZE,
  });

  const { loading, result } = usePaginationRequest<
    IGetAllProductRequest,
    IProduct[]
  >(
    request,
    ProductApiService.getAllProducts,
    [],
    {
      refresh: refresh,
    },
    () => {
      setRefresh(false);
    }
  );

  const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onPressEditProduct = (item: any) => {
    delete item["no"];
    history.push(`${location?.pathname}/edit/${item.id}`);
  };

  const onPressDeleteProduct = (item: any) => {
    setItemSelected(item);
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };
  const handleDeleteProduct = () => {
    _deleteProduct(itemSelected?.id!);
  };
  const deleteProductSuccessfully = action.includes(
    ProductActions.deleteProduct.successName
  );

  const _deleteProduct = (id: string) => {
    dispatch(
      ProductActions.deleteProduct.request({
        id: id,
      })
    );
  };

  const _clearReducer = () => {
    dispatch(ProductActions.clearReducer.request());
  };

  const _handlePageChange = (pageNumber: number) => {
    setRequest({ ...request, pageNumber: pageNumber });
  };
  const _handleSearchTextChange = (searchText: string) => {
    setRequest({ ...request, pageNumber: FIRST_PAGE, searchText: searchText });
  };
  const _handlePageSizeChange = (pageSize: number) => {
    setRequest({ ...request, pageSize: pageSize });
  };
  const _handleTypeChange = (callback: {
    value?: EProductType;
    hasQRCode?: any;
    isActive?: any;
  }) => {
    setCurrentProductType(callback.value);
    setCurrentQRType(callback.hasQRCode);
    setCurrentActive(callback.isActive);
    const params = {
      ...request,
      pageNumber: FIRST_PAGE,
      type: callback.value === EProductType.ALL ? "" : callback.value,
      hasQrCode: callback.hasQRCode === "A" ? null : callback.hasQRCode,
      isActive: callback.isActive,
    };
    if (callback.value === EProductType.ALL) {
      delete params["type"];
    }
    if (callback.hasQRCode === "A") {
      delete params["hasQrCode"];
    }
    if (callback.isActive === "A") {
      delete params["isActive"];
    }
    setRequest(params);
  };

  const _downloadProductMasterData = () => {
    const params: IDownloadProductMasterDataRequest = {
      ...request,
      pageSize: (result as IPaginateApiResponse<IProduct[]>).totalRecords,
      pageNumber: FIRST_PAGE,
      fileName: EXPORT_FILE_NAME.productMasterData,
    };
    dispatch(DownloadActions.downloadProductMasterData.request(params));
  };

  useEffect(() => {
    if (deleteProductSuccessfully) {
      setRefresh(true);
      setOpen(false);
      _clearReducer();
    }
  }, [action]);

  useEffect(() => {
    return () => {
      _clearReducer();
    };
  }, []);

  const COLUMNS: GridColDef[] = useMemo(() => {
    return [
      TableHelper.renderSTT(),
      TableHelper.createCell({
        field: "productCode",
        headerName: I18nTranslator(translations.masterData.productId),
        size: "small",
        align: "left",
        sortable: true,
      }),
      TableHelper.createCell({
        field: "description",
        headerName: I18nTranslator(translations.masterData.productName),
        size: "full",
        align: "left",
        sortable: true,
      }),
      TableHelper.createCell({
        field: "hasQRCode",
        headerName: I18nTranslator(translations.masterData.hasQRCode),
        size: 100,
        align: "center",
        renderCell: ({ value }) => <Checkbox checked={value} />,
      }),
      TableHelper.createCell({
        field: "type",
        headerName: I18nTranslator(translations.masterData.type),
        size: "small",
        align: "left",
        renderCell: ({ value }) => {
          return (
            <Typography
              style={{
                backgroundColor: ColorHelper.getProductTypeColor(
                  value as EProductType
                ),
                padding: "3px 16px",
                borderRadius: "3px",
                color: "white",
                fontWeight: "bold",
              }}
            >
              {StringHelper.getProductTypeName(value)}
            </Typography>
          );
        },
      }),
      TableHelper.createCell({
        field: "uom",
        headerName: I18nTranslator(translations.masterData.uom),
        size: "small",
      }),
      TableHelper.createCell({
        field: "quantityByUnit",
        headerName: I18nTranslator(translations.masterData.packSize),
        size: 130,
        align: "right",
        sortable: true,
      }),

      TableHelper.createCell({
        field: "palletQty",
        headerName: I18nTranslator(translations.masterData.palletSpec),
        size: 130,
        align: "right",
        renderCell: ({ row: { palletQty } }) => {
          const value = palletQty === 0 ? "" : palletQty;
          return <Typography>{value}</Typography>;
        },
      }),
      TableHelper.createCell({
        field: "level1Qty",
        headerName: I18nTranslator(translations.masterData.specLv1),
        size: 130,
        align: "right",
        renderCell: ({ row: { level1Qty } }) => {
          const value = level1Qty === 0 ? "" : level1Qty;
          return <Typography>{value}</Typography>;
        },
      }),
      TableHelper.createCell({
        field: "level2Qty",
        headerName: I18nTranslator(translations.masterData.specLv2),
        size: 130,
        align: "right",
        renderCell: ({ row: { level2Qty } }) => {
          const value = level2Qty === 0 ? "" : level2Qty;
          return <Typography>{value}</Typography>;
        },
      }),
      TableHelper.createCell({
        field: "level3Qty",
        headerName: I18nTranslator(translations.masterData.specLv3),
        size: 130,
        align: "right",
        renderCell: ({ row: { level3Qty } }) => {
          const value = level3Qty === 0 ? "" : level3Qty;
          return <Typography>{value}</Typography>;
        },
      }),

      TableHelper.createCell({
        field: "level4Qty",
        headerName: I18nTranslator(translations.masterData.specLv4),
        size: 130,
        align: "right",
        renderCell: ({ row: { level4Qty } }) => {
          const value = level4Qty === 0 ? "" : level4Qty;
          return <Typography>{value}</Typography>;
        },
      }),
      TableHelper.createCell({
        field: "productCodeOld",
        headerName: I18nTranslator(translations.masterData.productCodeOld),
        size: "small",
        align: "left",
        sortable: true,
      }),

      TableHelper.renderActivationCell("status"),
    ];
  }, [result]);
  return (
    <Fragment>
      <AppTable<IProduct>
        title={I18nTranslator(translations.masterData.product)}
        loading={loading}
        data={result}
        columns={COLUMNS}
        onAddNewClick={() => history.push(`${location?.pathname}/new`)}
        onSearch={_handleSearchTextChange}
        searchPlaceholder={I18nTranslator(
          translations.placeholder.searchProduct
        )}
        hideImport
        onChangePage={_handlePageChange}
        onRowPressEdit={onPressEditProduct}
        onRowPressDelete={onPressDeleteProduct}
        onDownloadClick={_downloadProductMasterData}
        hideFilter={false}
        onPressFilter={handleFilterClick}
        actionColumnProps={{ hideImport: true }}
        filterBadgeNumber={currentProductType === EProductType.ALL ? 0 : 1}
        onPageSizeChange={_handlePageSizeChange}
        isDownloading={downloading || loading}
      />
      <DeleteConfirmDialog
        open={open}
        value={itemSelected?.description}
        handleClose={handleClose}
        handleDelete={handleDeleteProduct}
        isDeleting={action.includes(ProductActions.deleteProduct.requestName)}
      />
      <FilterPopup
        currentType={{
          type: currentProductType,
          hasQrCode: currentQRType,
          isActive: currentActive,
        }}
        anchor={anchorEl}
        onClose={() => setAnchorEl(null)}
        onSave={_handleTypeChange}
      />
    </Fragment>
  );
};

export default ProductConfiguration;
